import { createEntityAdapter, createSelector, createSlice, EntityId, PayloadAction, Update } from '@reduxjs/toolkit';
import { ExternalUploadAsset } from '@ff-os/pdp-shared-redux/apis/external-upload/externalUploaderPoller';
import { uploadPrivateAsset } from '@ff-os/pdp-shared-redux/apis/private-assets/uploadPrivateAsset';
import { PrivateAssetUploadAction } from '@ff-os/shared-utils/types/private-assets/PrivateAssetUploadAction';
import { FulfilledUploadAction, PendingUploadAction, RejectedUploadAction } from '@ff-os/shared-utils/types/private-assets/uploadAction';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { GiftUploadContext } from '@ff-scene-plugins/gift-shared/GiftUploadContext';
import { variablePlugins } from '@ff-scene-plugins/gift-shared/registry';
import { toActiveInputs } from '@ff-scene-plugins/gift-shared/toActiveInputs';
import { GiftPdpState } from 'plugins/scenes/gift/redux/src/store';
import { bindInputSelectorFactory, bindInputUpdaterFactory } from '../createInputsBindings';
import { GiftExternalUploadPayload } from '../types/GiftExternalUploader';

const adapter = createEntityAdapter<GiftJson.Input<any>>();
const adapterSelectors = adapter.getSelectors<GiftPdpState>(s => s.project.inputs);

export const { actions, reducer } = createSlice({

    name: 'inputs',
    initialState: adapter.getInitialState(),

    reducers: {

        setAll: adapter.setAll,

        updateMany: (state, action: PayloadAction<readonly Update<GiftJson.Input<any>>[]>) => {
            for (const update of action.payload) {
                const input = state.entities[update.id];
                if (input) {
                    input.value = {
                        ...input.value,
                        ...update.changes
                    };
                }
            }
        },

        upsertMany: (state, action: PayloadAction<readonly GiftJson.Input<any>[]>) => {
            for (const update of action.payload) {
                const input = state.entities[update.id];
                if (input) {
                    input.value = { ...input.value, ...update.value };
                } else {
                    state.ids.push(update.id);
                    state.entities[update.id] = update;
                }
            }
        },
        addExternalUploads: (
            state,
            action: PayloadAction<ExternalUploadAsset<GiftExternalUploadPayload>[]>
        ) => {
            for (const asset of action.payload) {
                const payload = asset.externalUploaderPayload;
                const variableId = payload.variableIds[0];
                const input = state.entities[variableId]!;
                const { privateUploadHandler } = variablePlugins.require(payload.pluginId);
                privateUploadHandler!.externalUpload(input, asset);
            }
        }
    },

    extraReducers: builder => {

        builder.addCase(
            uploadPrivateAsset.pending,
            (state, action: PendingUploadAction<GiftUploadContext<unknown>>) => {
                const requestMeta = action.meta.arg;
                const input = state.entities[requestMeta.context.variableId]!;
                const { privateUploadHandler } = variablePlugins.require(requestMeta.context.pluginId);
                privateUploadHandler!.pending(input, requestMeta.context.variableData);
            });

        builder.addCase(
            uploadPrivateAsset.rejected,
            (state, action: RejectedUploadAction<GiftUploadContext<unknown>>) => {
                const requestMeta = action.meta.arg as PrivateAssetUploadAction<GiftUploadContext<unknown>>;
                const input = state.entities[requestMeta.context.variableId]!;
                const { privateUploadHandler } = variablePlugins.require(requestMeta.context.pluginId);
                privateUploadHandler!.error(input, requestMeta.context.variableData);
            });

        builder.addCase(
            uploadPrivateAsset.fulfilled,
            (state, action: FulfilledUploadAction<GiftUploadContext<unknown>>) => {
                const requestMeta = action.meta.arg;
                const input = state.entities[requestMeta.context.variableId]!;
                const { privateUploadHandler } = variablePlugins.require(requestMeta.context.pluginId);
                privateUploadHandler!.ready(input, requestMeta.context.variableData, action.payload);
            });
    }
});

export const selectActiveInputs = createSelector([
    (state: GiftPdpState) => state.project.variables.ids,
    (state: GiftPdpState) => state.project.inputs.entities
], toActiveInputs);

export const createInputBindings = <TInput>() => ({
    createInputSelector: bindInputSelectorFactory(adapterSelectors).to<TInput>(),
    createInputUpdater: bindInputUpdaterFactory(actions.updateMany).to<TInput>(),
    selectInputById: (state: GiftPdpState, id: EntityId) =>
        adapterSelectors.selectById(state, id)!.value as TInput
});